import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserProfile = ({ user, onClose, onEdit, isOpen }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  if (!isOpen || !user) return null;

  const handlePasswordReset = async () => {
    try {
      setIsResettingPassword(true);
      // Import userService dynamically to avoid circular dependency
      const { userService } = await import('../../../services/userService');
      const result = await userService?.resetUserPassword(user?.id, user?.email);
      
      if (result?.success) {
        alert(`Password reset successful!\n\nTemporary Password: ${result?.tempPassword}\n\nPlease share this with the user securely. They should change it immediately after login.`);
      } else {
        alert('Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      alert('Error resetting password: ' + error?.message);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success text-success-foreground', label: 'Active' },
      inactive: { color: 'bg-warning text-warning-foreground', label: 'Inactive' },
      suspended: { color: 'bg-error text-error-foreground', label: 'Suspended' },
      pending: { color: 'bg-secondary text-secondary-foreground', label: 'Pending' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { color: 'bg-primary text-primary-foreground', label: 'Administrator' },
      manager: { color: 'bg-accent text-accent-foreground', label: 'Manager' },
      editor: { color: 'bg-secondary text-secondary-foreground', label: 'Editor' },
      viewer: { color: 'bg-muted text-muted-foreground', label: 'Viewer' },
      user: { color: 'bg-muted text-muted-foreground', label: 'User' }
    };

    const config = roleConfig?.[role] || roleConfig?.user;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-300 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium text-lg">
              {user?.fullName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">{user?.fullName || 'User'}</h2>
              <p className="text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handlePasswordReset}
              iconName="Key"
              iconPosition="left"
              disabled={isResettingPassword}
            >
              {isResettingPassword ? 'Resetting...' : 'Reset Password'}
            </Button>
            <Button
              variant="outline"
              onClick={() => onEdit(user)}
              iconName="Edit"
              iconPosition="left"
            >
              Edit User
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              iconSize={20}
            >
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'profile' ?'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name="User" size={16} />
              Profile
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'activity' ?'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name="Activity" size={16} />
              Activity
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Full Name</label>
                    <p className="text-foreground">{user?.fullName || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email Address</label>
                    <p className="text-foreground">{user?.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Phone Number</label>
                    <p className="text-foreground">{user?.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Department</label>
                    <p className="text-foreground">{user?.department || 'Not assigned'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Role</label>
                    <div className="mt-1">
                      {getRoleBadge(user?.role)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Status</label>
                    <div className="mt-1">
                      {getStatusBadge(user?.status)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Timezone</label>
                    <p className="text-foreground">{user?.timezone || 'America/New_York'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Activity</label>
                    <p className="text-foreground">{formatDate(user?.updatedAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Account Created</label>
                    <p className="text-foreground">{formatDate(user?.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Permissions Section */}
              {user?.permissions && (
                <div className="border-t border-border pt-6">
                  <h3 className="text-lg font-medium text-foreground mb-4">Permissions</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <Icon name={user?.permissions?.documentRead ? "CheckCircle2" : "XCircle"} 
                            size={16} 
                            className={user?.permissions?.documentRead ? "text-success" : "text-muted-foreground"} />
                      <span className="text-sm text-foreground">Document Read Access</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name={user?.permissions?.documentWrite ? "CheckCircle2" : "XCircle"} 
                            size={16} 
                            className={user?.permissions?.documentWrite ? "text-success" : "text-muted-foreground"} />
                      <span className="text-sm text-foreground">Document Write Access</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name={user?.permissions?.documentDelete ? "CheckCircle2" : "XCircle"} 
                            size={16} 
                            className={user?.permissions?.documentDelete ? "text-success" : "text-muted-foreground"} />
                      <span className="text-sm text-foreground">Document Delete Access</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name={user?.permissions?.userManagement ? "CheckCircle2" : "XCircle"} 
                            size={16} 
                            className={user?.permissions?.userManagement ? "text-success" : "text-muted-foreground"} />
                      <span className="text-sm text-foreground">User Management</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name={user?.permissions?.systemSettings ? "CheckCircle2" : "XCircle"} 
                            size={16} 
                            className={user?.permissions?.systemSettings ? "text-success" : "text-muted-foreground"} />
                      <span className="text-sm text-foreground">System Settings</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name={user?.permissions?.changeManagement ? "CheckCircle2" : "XCircle"} 
                            size={16} 
                            className={user?.permissions?.changeManagement ? "text-success" : "text-muted-foreground"} />
                      <span className="text-sm text-foreground">Change Management</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name={user?.permissions?.aiAnalysis ? "CheckCircle2" : "XCircle"} 
                            size={16} 
                            className={user?.permissions?.aiAnalysis ? "text-success" : "text-muted-foreground"} />
                      <span className="text-sm text-foreground">AI Analysis Access</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name={user?.permissions?.auditLogs ? "CheckCircle2" : "XCircle"} 
                            size={16} 
                            className={user?.permissions?.auditLogs ? "text-success" : "text-muted-foreground"} />
                      <span className="text-sm text-foreground">Audit Logs</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <div className="p-6">
              <div className="text-center py-12">
                <Icon name="Activity" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Activity Log Coming Soon</h3>
                <p className="text-muted-foreground">
                  Detailed activity logs for this user will be displayed here.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;